import { useApiAdminconfigsGetCollection } from '@r/api/generated/client.ts';
import type { ConfigConfigReadModel } from '@r/api/generated/client.schemas.ts';

/**
 * Extrait le tableau de configs depuis la réponse API.
 * L'API peut retourner soit un tableau brut [...], soit l'ancien format { data: [...], pagination }.
 */
export function getConfigsFromResponse(
  data: ConfigConfigReadModel[] | { data?: ConfigConfigReadModel[] } | undefined
): ConfigConfigReadModel[] | undefined {
  if (!data) return undefined;
  if (Array.isArray(data)) return data;
  return (data as { data?: ConfigConfigReadModel[] }).data;
}

/**
 * Hook personnalisé qui récupère toutes les configs disponibles avec leurs IDs
 * L'API retourne toutes les configs sans pagination (pas de paramètres perPage/page)
 * Le cache React Query évite les appels multiples si plusieurs composants utilisent ce hook
 * Utiliser getConfigsFromResponse(configsQuery.data) pour obtenir le tableau de configs.
 */
export function useAllConfigs() {
  return useApiAdminconfigsGetCollection(
    undefined, // Pas de paramètres de filtrage (type/key), récupère toutes les configs
    {
      query: {
        enabled: true,
        staleTime: 5 * 60 * 1000, // Considérer les données comme fraîches pendant 5 minutes
        retry: 2, // Réessayer en cas d'erreur
        refetchOnWindowFocus: false // Ne pas refetch automatiquement
      }
    }
  );
}

/**
 * Helper pour trouver une config par type et key
 * @param configs Liste des configs
 * @param type Type de la config (ex: 'core', 'style', 'media')
 * @param key Key de la config (ex: 'logo', 'is-reference-enabled')
 * @returns La config trouvée ou undefined
 */
export function findConfig(
  configs: ConfigConfigReadModel[] | undefined,
  type: string,
  key: string
): ConfigConfigReadModel | undefined {
  return configs?.find((config) => config.type === type && config.key === key);
}

/**
 * Helper pour créer un mapping de "type:key" vers config IRI
 * @param configs Liste des configs
 * @returns Map de "type:key" vers "/configs/{id}"
 */
export function createConfigsMap(
  configs: ConfigConfigReadModel[] | undefined
): Map<string, string> {
  const configsMap = new Map<string, string>();
  if (configs) {
    for (const config of configs) {
      if (config.id) {
        const key = `${config.type}:${config.key}`;
        configsMap.set(key, `/configs/${config.id}`);
      }
    }
  }
  return configsMap;
}
